import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import CancelIcon from '@material-ui/icons/Cancel';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';

const useStyles = makeStyles((theme) => ({
    root: {
        margin: 0,
        borderWidth: theme.typography.pxToRem(1),
        borderStyle: 'solid',
        borderRadius: theme.typography.pxToRem(15),
        display: 'flex',
        alignItems: 'center',
        lineHeight: theme.spacing(16 / 8),
        padding: 0,
        height: theme.spacing(32 / 8),
        justifyContent: 'space-between',
        letterSpacing: theme.spacing(0.4 / 8),
        position: 'relative',
        outline: 'none',
        cursor: (props: StyleProps): string => (props.chipClickable
            ? 'pointer'
            : 'cursor'),
        '&:hover, &:focus': {
            backgroundColor: (props: StyleProps): string => ((props.chipClickable)
                ? 'none'
                : 'none'),
        },
        '&::after': {
            content: '""',
            width: '100%',
            height: '100%',
            position: 'absolute',
            borderRadius: 'inherit',
            top: 0,
            left: 0,
            backgroundColor: '#000000',
            zIndex: -1,
        },
        '&::selection': {
        },
    },
    iconWrap: {
        width: theme.spacing(28 / 8),
        height: theme.spacing(28 / 8),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: theme.spacing(1 / 8),
    },
    icon: {
        cursor: 'pointer',
        '&:hover, &:focus': {
            width: theme.spacing(28 / 8),
            height: theme.spacing(28 / 8),
        },
    },
    values: {
        marginLeft: theme.spacing(6 / 8),
    },
    label: {
        '&::selection': {
        },
    },
    labelAndValuesWrap: {
        justifyContent: 'space-between',
        display: 'flex',
        alignItems: 'center',
        marginLeft: theme.spacing(12 / 8),
        marginRight: (props: StyleProps): number | string => (props.hasIcon ? theme.spacing(6 / 8) : theme.spacing(12 / 8)),
        '& ::selection': {
        },
    },
}));

interface StyleProps {
    chipClickable: boolean;
    clicked: boolean;
    moreValuesMode: boolean;
    hasIcon: boolean;
}

interface PropsCommon {
    clicked?: boolean;
    label: string;
    onRemove?: () => void;
}

interface PropsWithValues extends PropsCommon {
    onCollapse: () => void;
    onExpand: (event: React.MouseEvent<HTMLElement>) => void;
    values: string[];
}

interface PropsWithoutValues extends PropsCommon {
    onCollapse?: () => void;
    onExpand?: (event: React.MouseEvent<HTMLElement>) => void;
    values?: undefined;
}

export type Props = PropsWithValues | PropsWithoutValues;

const FilterChip: React.FC<Props> = (props) => {
    const {
        clicked = false,
        label,
        onCollapse = undefined,
        onExpand = undefined,
        onRemove = undefined,
        values = null,
    } = props;

    const moreValuesMode = (values !== null);
    const chipClickable = (onExpand !== undefined);
    const hasIcon = onRemove !== undefined || clicked;
    const classes = useStyles({
        chipClickable,
        clicked,
        moreValuesMode,
        hasIcon,
    });

    return (
        <button
            type="button"
            className={classes.root}
            onClick={(event): void => {
                event.stopPropagation();
                if (!chipClickable) {
                    return;
                }
                if (clicked && onCollapse !== undefined) {
                    onCollapse();
                }
                if (!clicked && onExpand !== undefined) {
                    onExpand(event);
                }
            }}
        >
            <div className={classes.labelAndValuesWrap}>
                <span className={classes.label}>{label}</span>
                {values !== null && (
                    <>
                        <span>: </span>
                        <span className={classes.values}>
                            {values.map((value) => (
                                <span key={value}>{value}</span>
                            ))}
                        </span>
                    </>
                )}
            </div>
            {(hasIcon)
                && (
                    <div className={classes.iconWrap}>
                        {onRemove !== undefined && !clicked && (
                            <CancelIcon
                                onClick={(event): void => {
                                    event.stopPropagation();
                                    onRemove();
                                }}
                                className={classes.icon}
                            />
                        )}
                        {onCollapse !== undefined && clicked && (
                            <ArrowDropUpIcon
                                onClick={(event): void => {
                                    event.stopPropagation();
                                    onCollapse();
                                }}
                                className={classes.icon}
                            />
                        )}
                    </div>
                )}
        </button>
    );
};

export default FilterChip;
